// What other parts add to the Case screen, given by the host to
// `casesScreen({ stepMenu })` (the Agent chat part adds "Hand off to Agent" to
// a step's "…"). The Case screen knows nothing of what the rows do, and the
// other part does not import the Case screen's code beyond these types.
//
// Promise: a row an extension gives shows first in the step's "…" (it is the
// step's main next move), and writes through the Case's own update (shown at
// once, put back on a refusal, the refusal told under the step's row).
import type { CaseStep } from "@trameria/core/cases-shared";
import { createContext, type ReactNode, useContext } from "react";
import type { MenuItem } from "../ui/menu";

/** What a row of a step's "…" gets to act on. */
export interface CaseStepMenuProps {
  caseId: string;
  step: CaseStep;
  /**
   * Writes the Case with the extension's own inputs (`delegate_steps`), the
   * same update the screen uses. Resolves with the Case as written; a refusal
   * is already told under the step's row (the promise rejects too).
   */
  write(
    input: Readonly<Record<string, unknown>>,
    lead: string,
  ): Promise<unknown>;
}

/**
 * A hook giving the rows an extension adds to a step's "…" (none for a step it
 * has nothing for). Called on every render of the step's row, always.
 */
export type UseStepMenuItems = (
  props: CaseStepMenuProps,
) => readonly MenuItem[];

export interface CasesExtensions {
  stepMenu?: UseStepMenuItems;
}

const none: UseStepMenuItems = () => [];

const Context = createContext<UseStepMenuItems>(none);

export function CasesExtensionsProvider({
  extensions,
  children,
}: {
  extensions: CasesExtensions;
  children: ReactNode;
}) {
  return (
    <Context.Provider value={extensions.stepMenu ?? none}>
      {children}
    </Context.Provider>
  );
}

/** The hook of the step's "…" rows this host added (stable for the app's life). */
export const useStepMenuHook = (): UseStepMenuItems => useContext(Context);
